import React, { useCallback, useState } from "react";
import QRScreen from "./QRScreen";
import styled, { css, withTheme } from "styled-components";
import LaunchAppDialog from "./LaunchAppDialog";
import { IconButton } from "@vitalsource/vstui";
import { XLarge } from "@vitalsource/vstui-icons";
import { useIntl } from "react-intl";
import { useBrand } from "../../../../../../common/providers/brand/brand-context";
import ReaderDialog, { ReaderDialogType } from "../../common/ReaderDialog";
import useReadOffline from "../../../../framework/connectors/read-offline/useReadOffline";

interface GradientColors {
  dark: string;
  light: string;
}

const Styled = {
  CustomFullScreenDialog: styled(ReaderDialog)<{
    colors: GradientColors;
  }>`
    overflow: auto;
    ${({ colors }) =>
      colors &&
      css`
        background-image: linear-gradient(transparent 63%, white 37%),
          linear-gradient(to left, ${colors.light}, ${colors.dark});
      `}
  `,
  Header: styled.div`
    position: relative;
    width: 100%;
    height: 32px;
  `,
  CloseButton: styled(IconButton)`
    position: relative;
    float: right;
    margin: 24px 32px;
    button:focus {
      background-color: transparent !important;
    }
  `,
};

interface Props {
  open: boolean;
  onRequestClose: () => void;
  theme: any;
}

function QRDownloadApps({ open, onRequestClose, theme }: Props) {
  const intl = useIntl();
  const brand = useBrand();
  const { launchApp } = useReadOffline();
  const [launchAppParams, setLaunchAppParams] = useState({
    open: false,
    downloadUrl: "",
  });

  const launchAppDialog = useCallback(
    (args) => {
      launchApp();
      setLaunchAppParams({ ...args, open: true });
      onRequestClose?.();
    },
    [launchApp, onRequestClose]
  );

  const onRequestCloseLaunch = useCallback(() => {
    setLaunchAppParams({ open: false, downloadUrl: "" });
  }, [setLaunchAppParams]);

  return (
    <>
      <Styled.CustomFullScreenDialog
        type={ReaderDialogType.FullScreen}
        label={intl.formatMessage(
          { id: "QRDownloadApps.ariaLabel" },
          {
            brandName: brand.getProp("name"),
          }
        )}
        colors={{
          dark: theme.vstui.color.primary.getShade(7),
          light: theme.vstui.color.primary.getShade(5),
        }}
        open={open}
        onRequestClose={onRequestClose}
      >
        {open && (
          <>
            <Styled.Header>
              <Styled.CloseButton
                color="custom"
                customIconColor="#FFFFFF"
                size={"small"}
                onClick={onRequestClose}
                icon={<XLarge />}
                label={intl.formatMessage({ id: "base.close" })}
              />
            </Styled.Header>
            <QRScreen onRequestLaunchApp={launchAppDialog} />
          </>
        )}
      </Styled.CustomFullScreenDialog>
      <LaunchAppDialog
        {...launchAppParams}
        onRequestClose={onRequestCloseLaunch}
      />
    </>
  );
}

export default withTheme(QRDownloadApps);
